import { reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import type { FormRules } from 'element-plus';
import { ajax } from '@/api';
import type { LevelItem } from './types';

type AuthMode = 'login' | 'register';

type AuthStoreLike = {
  loginKey?: string;
  setLoginKey: (value: string) => void;
  removeLoginKey: (sync?: boolean) => void;
};

type UserDataLike = {
  setLoginKey: (value: string) => void;
  setName: (value: string) => void;
  setImgHead: (value: string) => void;
  setImgDecoration: (value: string) => void;
  removeAll: () => void;
};

export const useLevelExplorerAuth = (options: {
  authStore: AuthStoreLike;
  userData: UserDataLike;
  mineLevels: { value: LevelItem[] };
  favoriteLevels: { value: LevelItem[] };
  showProfile: { value: boolean };
  loadVisitDashboard: () => void;
}) => {
  const { authStore, userData, mineLevels, favoriteLevels, showProfile, loadVisitDashboard } = options;
  const showLogin = ref(false);
  const loginLoading = ref(false);
  const authMode = ref<AuthMode>('login');
  const loginForm = reactive({
    username: '',
    password: ''
  });

  const loginRules: FormRules = {
    username: [
      { required: true, message: '请输入账号', trigger: 'blur' },
      { pattern: /^[a-zA-Z0-9]{10}$/, message: '账号只能是10位字母或数字', trigger: 'blur' }
    ],
    password: [
      { required: true, message: '请输入密码', trigger: 'blur' },
      { min: 8, max: 20, message: '密码长度必须为8-20位', trigger: 'blur' },
      { pattern: /^[a-zA-Z0-9.]+$/, message: '密码只能包含字母、数字和.', trigger: 'blur' }
    ]
  };

  const openLoginDialog = () => {
    authMode.value = 'login';
    showLogin.value = true;
  };

  const requireLogin = () => {
    if (!sessionStorage.getItem('loginKey')) {
      openLoginDialog();
      ElMessage.warning('请先登录后再操作');
      return false;
    }
    return true;
  };

  const login = () => {
    ajax({
      url: 'user/login',
      method: 'POST',
      data: {
        username: loginForm.username,
        password: loginForm.password
      },
      success: (res: any) => {
        loginLoading.value = false;
        if (res.code !== 0) {
          ElMessage.error(res.msg);
          return;
        }
        const data = res.data || {};
        const token = data.token || data.loginKey || '';
        const name = data.name || data.username || loginForm.username;
        authStore.setLoginKey(token);
        userData.setLoginKey(token);
        userData.setName(name);
        if (data.imgHead) userData.setImgHead(data.imgHead);
        if (data.imgDecoration) userData.setImgDecoration(data.imgDecoration);
        sessionStorage.setItem('userId', String(data.id || ''));
        sessionStorage.setItem('role', data.role || 'user');
        showLogin.value = false;
        loginForm.password = '';
        loadVisitDashboard();
        ElMessage.success(res.msg || '登录成功');
      },
      error: () => {
        loginLoading.value = false;
        ElMessage.error('登录失败，请稍后重试');
      }
    });
  };

  const register = () => {
    ajax({
      url: 'user/register',
      method: 'POST',
      data: {
        username: loginForm.username,
        password: loginForm.password
      },
      success: (res: any) => {
        if (res.code !== 0) {
          loginLoading.value = false;
          ElMessage.error(res.msg);
          return;
        }
        ElMessage.success('注册成功，正在登录');
        login();
      },
      error: () => {
        loginLoading.value = false;
        ElMessage.error('注册失败，请稍后重试');
      }
    });
  };

  const submitAuth = () => {
    loginLoading.value = true;
    if (authMode.value === 'register') {
      register();
      return;
    }
    login();
  };

  const logout = () => {
    authStore.removeLoginKey(false);
    userData.removeAll();
    sessionStorage.removeItem('userId');
    sessionStorage.removeItem('role');
    authMode.value = 'login';
    showLogin.value = true;
    mineLevels.value = [];
    favoriteLevels.value = [];
    showProfile.value = false;
    loadVisitDashboard();
    ElMessage.success('已退出登录');
  };

  return {
    showLogin,
    loginLoading,
    authMode,
    loginForm,
    loginRules,
    openLoginDialog,
    requireLogin,
    submitAuth,
    logout
  };
};
